import AddIcon from '@mui/icons-material/Add'
import { Autocomplete, Box, Button, Chip, Popover, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api } from '../api/client'
import { keys, useLabels, useSetLabels } from '../api/hooks'
import type { Label } from '../api/types'
import { labelTree } from '../lib/cache'
import { LABEL_COLORS } from '../lib/labelColors'
import { useUi } from '../store/ui'

interface Props { anchor: HTMLElement | null; onClose: () => void; messageIds: string[]; current: string[] }

type Option = { id: string; path: string; color: string; create?: boolean }

/** Multi-select label picker with inline create + color palette. Applies changes optimistically. */
export function LabelPicker({ anchor, onClose, messageIds, current }: Props) {
  const labels = useLabels()
  const qc = useQueryClient()
  const { apply } = useSetLabels()
  const notify = useUi((s) => s.notify)
  const [input, setInput] = useState('')
  const [color, setColor] = useState(LABEL_COLORS[5]!)
  const options = useMemo<Option[]>(() => labelTree(labels.data ?? []).map((l) => ({ id: l.id, path: l.path, color: l.color })), [labels.data])
  const value = options.filter((o) => current.includes(o.id))
  const exists = options.some((o) => o.path.toLowerCase() === input.trim().toLowerCase())

  const createAndApply = async () => {
    const name = input.trim()
    if (!name) return
    try {
      // "Parent/Child" creates the nested label under an existing parent when one matches.
      const parts = name.split('/').map((p) => p.trim()).filter(Boolean)
      let parentId: string | null = null
      let made: Label | undefined
      for (const [i, part] of parts.entries()) {
        const path = parts.slice(0, i + 1).join('/').toLowerCase()
        const existing = options.find((o) => o.path.toLowerCase() === path)
        if (existing) { parentId = existing.id; continue }
        made = await api<Label>('/labels', { method: 'POST', body: { name: part, color, parentId } })
        parentId = made.id
      }
      await qc.invalidateQueries({ queryKey: keys.labels })
      if (parentId) apply(messageIds, [parentId], [], false)
      setInput('')
    } catch (e) {
      notify({ message: e instanceof Error ? e.message : 'Could not create label', severity: 'error' })
    }
  }

  return (
    <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={onClose} slotProps={{ paper: { sx: { width: 320, p: 2 } } }}>
      <Typography variant="subtitle2" gutterBottom>Label as</Typography>
      {input.trim() && !exists && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }} role="radiogroup" aria-label="Label color">
            {LABEL_COLORS.map((c) => (
              <Box key={c} component="button" type="button" role="radio" aria-checked={c === color} aria-label={`Color ${c}`} onClick={() => setColor(c)}
                sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: c, border: c === color ? '2px solid' : '2px solid transparent', borderColor: c === color ? 'text.primary' : 'transparent', cursor: 'pointer', p: 0 }} />
            ))}
          </Box>
          <Button size="small" startIcon={<AddIcon />} onMouseDown={(e) => e.preventDefault()} onClick={() => void createAndApply()}>Create “{input.trim()}”</Button>
        </Box>
      )}
      <Autocomplete<Option, true, false, false>
        multiple
        autoHighlight
        forcePopupIcon={false}
        clearOnBlur={false}
        options={options}
        value={value}
        inputValue={input}
        onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v) }}
        getOptionLabel={(o) => o.path}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderValue={(tags, getProps) => tags.map((t, i) => { const { key, ...p } = getProps({ index: i }); return <Chip {...p} key={key} label={t.path} sx={{ bgcolor: t.color, color: '#fff' }} /> })}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: o.color, mr: 1.5 }} />
            {o.path}
          </li>
        )}
        onChange={(_, next) => {
          const nextIds = new Set(next.map((o) => o.id))
          const add = [...nextIds].filter((id) => !current.includes(id))
          const remove = current.filter((id) => !nextIds.has(id))
          if (add.length || remove.length) apply(messageIds, add, remove, false)
        }}
        renderInput={(params) => <TextField {...params} autoFocus placeholder="Search or create…" onKeyDown={(e) => { if (e.key === 'Enter' && input.trim() && !exists && !options.some((o) => o.path.toLowerCase().includes(input.trim().toLowerCase()))) { e.preventDefault(); void createAndApply() } }} />}
        noOptionsText={input.trim() ? 'No matching label' : 'No labels yet'}
        slotProps={{ listbox: { sx: { maxHeight: 200 } } }}
      />
    </Popover>
  )
}
